import clsx from 'clsx'
import { CornerDownRight, Pencil } from 'lucide-react'
import { useNavigate } from 'react-router'

import { partnersQuotes } from 'rails_routes'
import { Button, ButtonGroup, IconButton, useBreakpoint } from 'slab'
import { usePutQuotingProspectsId } from 'utils/web_api'
import { type PostQuotingProspectsReasonsForChangingInsuranceItem, QuotingProspectQuoteRequestStatus } from 'utils/web_api.schemas'

import { QuoteRequestStep } from '..'
import { READABLE_CHANGE_REASON } from '../BusinessDetailsForm'
import { useProspect } from '../ProspectProvider'
import { fileListFromDocuments } from '../utils'

interface SummaryPageSectionProps {
  title: string
  children?: React.ReactNode
}

const SummaryPageSection = ({
  title,
  children
}: SummaryPageSectionProps) => (
  <div>
    <h3 className='text-xs text-gray-500 mb-2'>{title}</h3>
    <div className='text-sm text-gray-800'>
      {children}
    </div>
  </div>
)

export function SummaryPage () {
  const { breakpointIsAtLeast } = useBreakpoint()
  const { prospect } = useProspect()
  const navigate = useNavigate()
  const isColumnView = breakpointIsAtLeast('md')

  if (!prospect) {
    return null
  }

  const {
    name,
    offers_health_insurance_today: offersBenefits,
    health_insurance_effective_date: expectedCoverageStartDate,
  } = prospect

  const { mutate: updateProspect } = usePutQuotingProspectsId({
    mutation: {
      onSuccess: () => {
        window.segment?.track('Completed quote request flow', { id: prospect.id })
        // Return to TfB dashboard
        window.location.href = partnersQuotes.index({ success: 'Request submitted, the Thatch team will be in touch shortly!' })
      }
    }
  })

  const changeReasons = (prospect.reasons_for_changing_insurance ?? []).map(reason =>
    READABLE_CHANGE_REASON[reason as PostQuotingProspectsReasonsForChangingInsuranceItem])

  if (prospect.other_reason_for_changing_insurance) {
    changeReasons.push(prospect.other_reason_for_changing_insurance)
  }

  const onSubmit = () => {
    updateProspect({ id: prospect.id, data: { completed_quote_request_step: QuotingProspectQuoteRequestStatus.summary } })
  }

  return (
    <div>
      <h1 className='text-2xl font-medium text-gray-800 mb-10'>Summary</h1>
      <div className={clsx('max-w-[880px] mx-auto mb-10', {
        'grid grid-cols-2 gap-12': isColumnView,
        'flex flex-col gap-8': !isColumnView
      })}
      >
        <div className={clsx('', {
          'relative after:absolute after:top-0 after:right-[-1rem] after:h-full after:w-px after:bg-gray-200': isColumnView
        })}
        >
          <div className='flex flex-row items-center gap-2 mb-4'>
            <h2 className='text-md'>Business details</h2>
            <IconButton label='Edit' icon={Pencil} onClick={() => navigate('./edit')} />
          </div>
          <div className='flex flex-col gap-4'>
            <SummaryPageSection title='Business Name'>{name}</SummaryPageSection>
            <SummaryPageSection title='Does this business currently offer benefits?'>{offersBenefits ? 'Yes' : 'No'}</SummaryPageSection>
            <SummaryPageSection title='Expected coverage start date'>{expectedCoverageStartDate}</SummaryPageSection>
            <SummaryPageSection title='Why are they looking to change?'>
              {changeReasons
                ? (
                  <ul className='list-disc list-inside'>
                    {changeReasons.map(reason => <li key={reason}>{reason}</li>)}
                  </ul>)
                : <p className='text-gray-500'>N/A</p>}
            </SummaryPageSection>
          </div>
        </div>
        <div>
          <h2 className='text-md mb-4'>Uploaded files</h2>
          <div className='flex flex-col gap-4'>
            <SummaryPageSection title='Census'>
              {fileListFromDocuments([prospect.census_upload!])}
            </SummaryPageSection>
            <SummaryPageSection title='SBC(s)'>
              {fileListFromDocuments(prospect.sbcs_uploads || [])}
            </SummaryPageSection>
            <SummaryPageSection title='Other files'>
              {fileListFromDocuments(prospect.other_file_uploads || [])}
            </SummaryPageSection>
          </div>
        </div>
        <div className='col-span-full'>
          <ButtonGroup align='right'>
            <Button
              variant='secondary'
              size='large'
              onClick={() => navigate(`../${QuoteRequestStep.OTHER_FILES_UPLOAD}`)}
            >
              Back
            </Button>
            <Button
              variant='primary'
              size='large'
              rightIcon={CornerDownRight}
              onClick={onSubmit}
            >
              Submit Quote Request
            </Button>
          </ButtonGroup>
        </div>
      </div>
    </div>
  )
}
